import React, { useState, useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
// components
import { FormattedMessage } from 'react-intl';
import Collapse from 'components/Collapse/Collapse';
import Line from 'components/Line';
import Translation from 'components/Translation';
import OralTreatmentLabel from 'components/DailyCheckups/TreatmentProductLabels/OralTreatmentLabel';
import InjectableTreatmentLabel from 'components/DailyCheckups/TreatmentProductLabels/InjectableTreatmentLabel';
import CollapseTitle from './CollapseTitle';
import Link from 'react-router/lib/Link';
// requests
import { getCheckupTreatments } from 'endpoints/dailyCheckups';
// utils
import classnames from 'classnames/bind';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { filterMedsByType, renderMentionsText } from 'utils';
// styles
import styles from './SectionCollapse.module.scss';

const cn = classnames.bind(styles);

const MedicationsCollapse = ({
  checkupId, pigGroupId, className, titleClassName, showCheckup, comment, isEdited, itemsCount, measurementSystem
}) => {
  const [{ isLoading, isCollapseOpened, isLoaded, treatments }, setState] = useState({
    isLoaded: false,
    isLoading: false,
    isCollapseOpened: false,
    treatments: [],
  });
  const hasContent = Boolean(comment) || Boolean(itemsCount);

  const loadTreatments = (isActual = () => true) => {
    setState((prevState) => ({ ...prevState, isLoading: true }));
    getCheckupTreatments(checkupId)
      .then((treatments) => {
        if (!isActual()) return;
        setState((prevState) => ({ ...prevState, treatments, isLoaded: true, isLoading: false }));
      })
      .catch((errors) => {
        if (!isActual()) return;
        setState((prevState) => ({ ...prevState, isLoading: false }));
        toastResponseErrors(errors);
      });
  };

  // Hooks run before the early return so their order is the same on every render,
  // the same instance renders different checkups when a notification switches the activity
  useEffect(() => {
    let isActual = true;
    setState((prevState) => ({ ...prevState, treatments: [], isLoaded: false, isLoading: false }));
    if (hasContent) loadTreatments(() => isActual);
    return () => {
      isActual = false;
    };
  }, [checkupId]);

  if (!hasContent) return null;

  const { injectable, oral, topical } = filterMedsByType(treatments);
  const isTriggered = Boolean(comment) || oral.length > 0;

  const toggleCollapse = (isCollapseOpened) => {
    if (!isLoaded) loadTreatments();
    setState((prevState) => ({ ...prevState, isCollapseOpened }));
  };

  return ((isTriggered || showCheckup) && (
    <Collapse
      className={cn(className, 'section-collapse', { 'highlighted': isTriggered && !isCollapseOpened })}
      titleClassName={titleClassName}
      title={(
        <CollapseTitle hasEditedBadge={isEdited} hasNotesBadge={Boolean(comment)} hasTriggerIcon={isTriggered}>
          <FormattedMessage tagName="b" id="general.medicationsCount" values={{ count: itemsCount }} />
        </CollapseTitle>
      )}
      onToggle={toggleCollapse}
      isLoading={isLoading}
    >
      <Line
        almostWhite
        hasTopBorder
        bolded
        label={<FormattedMessage id="general.meds.injectable" />}
        value={<FormattedMessage id="general.meds.head" />}
      />
      {injectable.length
        ? injectable.map((item) => (
          <Line
            key={item.id}
            boldedValue
            label={<InjectableTreatmentLabel product={item.treatment_product} quantity={item.quantity} />}
            value={item.head_count}
          />
        ))
        : (
          <Line isEmptyLine>
            <FormattedMessage id="general.none" />
          </Line>
        )}

      <Line
        almostWhite
        bolded
        label={<FormattedMessage id="general.meds.oral" />}
        value={<FormattedMessage id="general.meds.head" />}
      />
      {oral.length
        ? oral.map((item) => (
          <Line
            key={item.id}
            boldedValue
            label={(
              <Link to={`/barnsheets/groups/${pigGroupId}/treatments/history`} className="color-primary">
                <OralTreatmentLabel
                  product={item.treatment_product}
                  quantity={item.quantity}
                  gallonsValue={item.gallons_value}
                  measurementSystem={measurementSystem}
                />
              </Link>
            )}
            value={item.head_count}
          />
        ))
        : (
          <Line isEmptyLine>
            <FormattedMessage id="general.none" />
          </Line>
        )}

      <Line
        almostWhite
        bolded
        label={<FormattedMessage id="general.meds.topical" />}
        value={<FormattedMessage id="general.meds.head" />}
      />
      {topical.length
        ? topical.map((item) => (
          <Line
            key={item.id}
            boldedValue
            label={item.treatment_product.name}
            value={item.head_count}
          />
        ))
        : (
          <Line isEmptyLine>
            <FormattedMessage id="general.none" />
          </Line>
        )}

      {!!comment && (
        <>
          <Line almostWhite bolded>
            <FormattedMessage id="general.notes" />
          </Line>
          <Line lightYellow>
            <Translation text={comment}>{renderMentionsText(comment)}</Translation>
          </Line>
        </>
      )}
    </Collapse>
  ));
};

MedicationsCollapse.propTypes = {
  checkupId: T.number.isRequired,
  pigGroupId: T.number,
  className: T.string,
  titleClassName: T.string,
  showCheckup: T.bool,
  isEdited: T.bool,
  comment: T.string,
  itemsCount: T.number,
  measurementSystem: T.string,
};

export default connect(
  ({ auth }) => ({
    measurementSystem: auth.user.current_company.measurement_system,
  })
)(MedicationsCollapse);
